import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid2 as Grid,
} from "@mui/material";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import MosaicPlaceholder from "../components/MosaicPlaceholder";
import RegistrationForm from "../components/RegistrationForm";
import Course from "../models/Course";
import CourseService from "../services/courseService";
import useResizeObserver from "../hooks/useResizeObserver";
import useNotification from "../hooks/useNotification";
import { useLocation, useNavigate } from "react-router-dom";
import registrationService from "../services/registrationService";
import { styled } from "@mui/material/styles";

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: "auto", // This pushes the button to the bottom
  backgroundColor: "#00537E",
}));

const RegisterPage: React.FC = () => {
  const [ref] = useResizeObserver<HTMLDivElement>();
  const { showNotification } = useNotification();

  const [registrationCode, setRegistrationCode] = useState("");
  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [selectedCourseName, setSelectedCourseName] = useState<string | null>(
    null
  );
  const [isLoading] = useState(false); //isLoading is used for the code validation button

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [participantData, setParticipantData] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const courseId = queryParams.get("courseId");
    const registrationCode = queryParams.get("registrationCode");
    const invitationId = queryParams.get("invitationId");
    const courseType = queryParams.get("courseType");

    if (courseId && registrationCode && invitationId) {
      handleLinkRegistration(courseId, registrationCode, invitationId);
    }
    if (courseType) {
      setTabValue(courseType);
    }
    // eslint-disable-next-line
  }, [location]);

  const handleLinkRegistration = async (
    courseId: string,
    registrationCode: string,
    invitationId: string
  ) => {
    try {
      const validationResponse =
        await registrationService.validateRegistrationCode(registrationCode);
      if (validationResponse.status === 200) {
        const participantResponse =
          await registrationService.getParticipantData(invitationId);
        setParticipantData(participantResponse);
        setSelectedCourseId(parseInt(courseId));
        setSelectedCourseName(validationResponse.data.courseName);
        setRegistrationCode(registrationCode);
        setIsRegistrationFormOpen(true);
      } else {
        showNotification("Invalid or expired registration link.", "error");
        navigate("/register");
      }
    } catch (error) {
      console.error("Error processing registration link:", error);
      showNotification(
        "An error occurred while processing the registration link.",
        "error"
      );
      navigate("/register");
    }
  };

  const getTabLabel = (fullLabel: string, shortLabel: string) => {
    return isSmallScreen ? (
      <span style={{ fontSize: "x-large" }}>shortLabel</span>
    ) : (
      <span style={{ fontSize: "x-large" }}>{fullLabel}</span>
    );
  };

  const [tabValue, setTabValue] = useState("legacy");

  const SkeletonCard = () => (
    <Card
      sx={{ width: 300, height: 250, display: "flex", flexDirection: "column" }}
    >
      <CardContent>
        <Skeleton variant="text" width="80%" height={32} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="50%" />
        <Box sx={{ mt: 1 }}>
          <Skeleton variant="rectangular" width={120} height={24} />
        </Box>
      </CardContent>
      <CardActions sx={{ marginTop: "auto" }}>
        <Skeleton variant="rectangular" width={100} height={36} />
      </CardActions>
    </Card>
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<{
    title: string;
    content: string;
    confirmLabel: string;
    cancelLabel: string;
  } | null>(null);

  // Add console.log to check if the function is being called
  const fetchCoursesWithLog = async () => {
    console.log("Fetching courses...");
    const result = await CourseService.fetchCourses({
      limit: 500,
      visible: true,
    });
    console.log("Courses fetched:", result);
    if (result && result.courses && Array.isArray(result.courses)) {
      return result.courses;
    } else {
      console.error("courses is not an array or data is missing:", result);
      return [];
    }
  };

  const { data: courses, isLoading: isCoursesLoading } = useQuery<
    Course[],
    Error,
    Course[],
    ["courses"]
  >({
    queryKey: ["courses"],
    queryFn: fetchCoursesWithLog,
    select: (courseData: any) => {
      if (courseData) {
        return courseData.filter((course: Course) => course.visible);
      } else {
        console.error(
          "courses is not an array or data is missing:",
          courseData
        );
        return [];
      }
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Mutation for course registration
  const registrationMutation = useMutation({
    mutationFn: (data: {
      courseId: number | null;
      formData: any;
      code: string | undefined;
    }) =>
      registrationService.registerForCourse(
        data.courseId,
        data.formData,
        data.code
      ),
    onSuccess: (response) => {
      // Only called for 2xx responses
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      console.log("Code validation response:", response);
      showNotification(
        response.message,
        // "Registration request submitted successfully",
        "success"
      );
      handleRegistrationFormClose();
    },
    onError: (error: any) => {
      // Handle known error statuses
      if (error?.response?.status === 409) {
        showNotification(
          `You are already registered for this course. Current status: ${error.response.data.status}`,
          "info"
        );
      } else if (error?.response?.status === 403) {
        showNotification(
          "Course is full. Please try another course or contact the administrator.",
          "error"
        );
      } else {
        showNotification(
          "Failed to register for the course. Please try again.",
          "error"
        );
      }
      handleRegistrationFormClose();
    },
  });

  // Mutation for code validation
  const codeValidationMutation = useMutation({
    mutationFn: registrationService.validateRegistrationCode,
    onSuccess: (response) => {
      // Only called for 2xx responses
      console.log("Code validation response:", response);
      if (response.data.courseStatus === "WAITLIST") {
        setDialogContent({
          title: "Course Full - Waitlist Available",
          content: `The course "${response.data.courseName}" is full, but you can be added to the waitlist. Would you like to proceed with registration?`,
          confirmLabel: "Yes, add me to waitlist",
          cancelLabel: "No, cancel",
        });
        setDialogOpen(true);
      } else {
        setSelectedCourseId(response.data.courseId);
        setSelectedCourseName(response.data.courseName);
        setIsRegistrationFormOpen(true);
      }
    },
    onError: (error: any) => {
      if (error?.response?.status === 403) {
        showNotification(
          "This course is no longer available for registration.",
          "error"
        );
      } else if (error?.response?.status === 404) {
        showNotification(
          "Invalid registration code. Please check and try again.",
          "error"
        );
      } else {
        showNotification(
          "Failed to validate the registration code. Please try again.",
          "error"
        );
      }
    },
  });

  const handleRegister = (course: Course) => {
    setSelectedCourseId(course.id);
    setSelectedCourseName(course.name);
    setIsRegistrationFormOpen(true);
  };

  const handleRegistrationFormClose = () => {
    setIsRegistrationFormOpen(false);
    setSelectedCourseId(null);
    setSelectedCourseName(null);
    setRegistrationCode("");
  };

  const handleRegistrationFormSubmit = async (formData: any) => {
    /* try {
      const response = await registerForCourse(
        selectedCourseId,
        formData.submissionData,
        registrationCode
      );
      if (selectedCourseId) {
        await registerForCourse(selectedCourseId, formData.submissionData);
        await loadCourses(); // Reload courses after registration
      }

      if (response.status === 200) {
        showNotification(
          "Registration request submitted successfully",
          "success"
        );
      } else if (response.status === 409) {
        showNotification(
          `You are already registered for this course. Current status: ${response.data.status}`,
          "info"
        );
      } else if (response.status === 403) {
        showNotification(
          "Course is full. Please try another course or contact the administrator.",
          "error"
        );
      } else {
        showNotification(
          "An error occurred during registration. Please try again.",
          "error"
        );
      }

      await loadCourses(); // Reload courses after registration attempt
      handleRegistrationFormClose();
    } catch (error) {
      console.error("Failed to register for course:", error);
      showNotification(
        "Failed to register for the course. Please try again.",
        "error"
      );
    } */
    registrationMutation.mutate({
      courseId: selectedCourseId,
      formData: formData.submissionData,
      code: registrationCode,
    });
  };

  const handleCodeValidation = async () => {
    codeValidationMutation.mutate(registrationCode);
  };

  const handleDialogConfirm = () => {
    setDialogOpen(false);
    setSelectedCourseId(null); // We don't have a course ID for waitlist registration
    setSelectedCourseName(null);
    setIsRegistrationFormOpen(true);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setRegistrationCode("");
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const renderCourseCards = (courseType: string) => {
    console.log(courses);

    const filteredCourses = isCoursesLoading
      ? []
      : courses
          ?.filter((course: Course) => course.type === courseType)
          .sort((a, b) => (a.startDate < b.startDate ? -1 : 1)) ?? [];

    courseType = isMobile ? "mobile" : courseType;

    switch (courseType) {
      case "mobile":
        return (
          <TableContainer component={Paper} style={{ marginTop: "20px" }}>
            <Table
              sx={{ maxWidth: "100%", marginLeft: "0px", marginRight: "0px" }}
              aria-label="simple table"
            >
              <TableHead style={{ background: "#077098" }}>
                <TableRow>
                  <TableCell
                    style={{ color: "white", width: "40%", fontSize: "larger" }}
                    align="center"
                  >
                    Course Details
                  </TableCell>
                  <TableCell
                    style={{ color: "white", width: "20%", fontSize: "larger" }}
                    align="center"
                  >
                    Register
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isCoursesLoading ? (
                  <SkeletonCard key={0} />
                ) : (
                  filteredCourses.map((course: Course) => (
                    <TableRow
                      key={course.name}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell align="center">
                        <Grid container spacing={0}>
                          <Grid
                            size={12}
                            style={{
                              display: "flex",
                              justifyContent: "left",
                              alignItems: "center",
                            }}
                          >
                            {course.location}
                          </Grid>
                          <Grid
                            size={12}
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginBottom: "5px",
                            }}
                          >
                            {new Date(course.startDate).toLocaleDateString()}
                          </Grid>
                          <Grid size={12}>-</Grid>
                          <Grid
                            size={12}
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {new Date(course.endDate).toLocaleDateString()}
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell align="center">
                        {course.openEnroll ? (
                          <Button
                            variant="contained"
                            style={{ backgroundColor: "#00537E" }}
                            onClick={() => handleRegister(course)}
                          >
                            Register
                          </Button>
                        ) : (
                          <StyledButton
                            variant="contained"
                            color="primary"
                            disabled
                          >
                            Closed
                          </StyledButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        );
      default:
        return (
          <TableContainer component={Paper} style={{ marginTop: "20px" }}>
            <Table
              sx={{ maxWidth: "80%", marginLeft: "10%" }}
              aria-label="simple table"
            >
              <TableHead style={{ background: "#077098" }}>
                <TableRow>
                  <TableCell
                    style={{
                      color: "white",
                      width: "40%",
                      fontSize: "20px",
                      paddingLeft: "20px",
                    }}
                    align="left"
                  >
                    Location
                  </TableCell>
                  <TableCell>
                    {" "}
                    <Divider
                      style={{ borderColor: "white", height: "25px" }}
                      orientation="vertical"
                    />
                  </TableCell>
                  <TableCell
                    style={{ color: "white", width: "20%", fontSize: "20px" }}
                    align="center"
                  >
                    Start Date <Divider orientation="vertical" />
                  </TableCell>
                  <TableCell>
                    {" "}
                    <Divider
                      style={{ borderColor: "white", height: "25px" }}
                      orientation="vertical"
                    />
                  </TableCell>
                  <TableCell
                    style={{ color: "white", width: "20%", fontSize: "20px" }}
                    align="center"
                  >
                    End Date <Divider orientation="vertical" />
                  </TableCell>
                  <TableCell>
                    {" "}
                    <Divider
                      style={{ borderColor: "white", height: "25px" }}
                      orientation="vertical"
                    />
                  </TableCell>
                  <TableCell
                    style={{ color: "white", width: "20%", fontSize: "20px" }}
                    align="center"
                  >
                    Register
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isCoursesLoading ? (
                  <SkeletonCard key={0} />
                ) : (
                  filteredCourses.map((course: Course) => (
                    <TableRow
                      key={course.name}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell align="left" style={{ paddingLeft: "20px" }}>
                        {course.location}
                      </TableCell>
                      <TableCell align="center" />
                      <TableCell align="center">
                        {new Date(course.startDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center" />
                      <TableCell align="center">
                        {new Date(course.endDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center" />
                      <TableCell align="center">
                        {course.openEnroll ? (
                          <Button
                            variant="contained"
                            style={{
                              backgroundColor: "#00537E",
                              fontSize: "16px",
                            }}
                            onClick={() => handleRegister(course)}
                          >
                            Register
                          </Button>
                        ) : (
                          <StyledButton
                            variant="contained"
                            color="primary"
                            disabled
                            style={{ fontSize: "16px" }}
                          >
                            Register
                          </StyledButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        );
    }
  };

  const scrollIntoView = (elementId: string) => {
    const elem = document.getElementById(elementId);
    if (elem) {
      var top = elem.offsetTop - 130; //Getting Y of target element
      window.scrollTo(0, top);
    }
  };

  return (
    <Container
      ref={ref}
      style={{ maxWidth: "unset !important" }}
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: "800px",
      }}
    >
      <MosaicPlaceholder />
      <Box sx={theme.custom.pageTitle}></Box>

      {isMobile ? (
        <Grid
          container
          spacing={2}
          style={{ marginBottom: "15px", marginTop: "15px" }}
        >
          <Grid
            size={12}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <StyledButton
              onClick={() => scrollIntoView("legacy")}
              variant="contained"
              color="primary"
              fullWidth
            >
              Partner Training Class
            </StyledButton>
          </Grid>
          <Grid
            size={12}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <StyledButton
              onClick={() => scrollIntoView("tactical")}
              variant="contained"
              color="primary"
              fullWidth
            >
              Tactical
            </StyledButton>
          </Grid>
          {/* <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("online")} variant="contained" color="primary" fullWidth>
              Online Classes
            </StyledButton>
          </Grid> */}
        </Grid>
      ) : null}

      {isMobile ? (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Grid container spacing={2}>
            <Grid
              size={12}
              style={{ display: "flex", justifyContent: "start" }}
            >
              <Typography
                variant="body1"
                sx={{
                  transform: "translateY(15px)",
                  width: { xs: "100%", sm: "auto" },
                  mb: { xs: 1, sm: 0 },
                  display: "flex",
                  height: "100%",
                }}
                style={{ color: "#00537E", fontWeight: "500" }}
              >
                Register with Code
              </Typography>
            </Grid>
            <Grid
              size={8}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <TextField
                label="Registration Code"
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value)}
                inputProps={{ maxLength: 8 }}
                sx={{
                  width: { xs: "100%", sm: "200px" },
                  "& input": { textTransform: "uppercase" },
                }}
              />
            </Grid>
            <Grid
              size={4}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {registrationCode ? (
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: "#077098",
                    height: "100%",
                    margin: "0px",
                  }}
                  onClick={handleCodeValidation}
                  disabled={!registrationCode || isLoading}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {isLoading ? "Validating..." : "Validate Code"}
                </Button>
              ) : (
                <StyledButton
                  style={{ height: "100%", margin: "0px" }}
                  variant="contained"
                  color="primary"
                  disabled
                >
                  Validate
                </StyledButton>
              )}
            </Grid>
          </Grid>
          <Divider
            style={{
              borderColor: "divider",
              height: "25px",
              marginBottom: "10px",
            }}
            orientation="horizontal"
          />
        </Box>
      ) : null}

      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflow: "hidden",
          }}
        >
          <Box id="legacy" sx={{ fontWeight: "900", color: "#00537E" }}>
            Partner Training Class
          </Box>
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {renderCourseCards("legacy")}
          </Box>
          <Box
            id="tactical"
            sx={{ fontWeight: "900", color: "#00537E", marginTop: "10px" }}
          >
            Tactical
          </Box>
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {renderCourseCards("tactical")}
          </Box>
          {/* <Box id="online" sx={{ fontWeight: "900", color: "#00537E", marginTop: "10px" }}>
            Online Class
          </Box>
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {renderCourseCards('online')}
          </Box> */}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflow: "hidden",
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="course type tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab
                style={{ textTransform: "capitalize" }}
                label={getTabLabel("Partner Training Class", "Legacy")}
                value="legacy"
              />
              <Tab icon={<Divider orientation="vertical" />} disabled />
              <Tab
                style={{ textTransform: "capitalize" }}
                label={getTabLabel("Tactical", "Tactical")}
                value="tactical"
              />
              {/* <Tab icon={<Divider orientation="vertical" />} disabled />
              <Tab label={getTabLabel("Online Course", "Online")} value="online" /> */}
            </Tabs>
          </Box>
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {renderCourseCards(tabValue)}
          </Box>
        </Box>
      )}

      {isMobile ? null : (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Typography
              variant="body1"
              sx={{
                verticalAlign: "center",
                width: { xs: "100%", sm: "auto" },
                mb: { xs: 1, sm: 0 },
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
              style={{ color: "#00537E", fontWeight: "500" }}
            >
              Register with Code
            </Typography>
            <TextField
              label="Registration Code"
              value={registrationCode}
              onChange={(e) => setRegistrationCode(e.target.value)}
              inputProps={{ maxLength: 8 }}
              sx={{
                width: { xs: "100%", sm: "200px" },
                "& input": { textTransform: "uppercase" },
              }}
            />
            {registrationCode ? (
              <Button
                variant="contained"
                style={{ backgroundColor: "#077098" }}
                onClick={handleCodeValidation}
                disabled={!registrationCode || isLoading}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {isLoading ? "Validating..." : "Validate Code"}
              </Button>
            ) : (
              <StyledButton variant="contained" color="primary" disabled>
                Validate Code
              </StyledButton>
            )}
          </Stack>
        </Box>
      )}
      {isRegistrationFormOpen && (
        <RegistrationForm
          open={isRegistrationFormOpen}
          onClose={handleRegistrationFormClose}
          onSubmit={handleRegistrationFormSubmit}
          registrationCode={registrationCode}
          selectedCourseName={selectedCourseName ?? ""}
          participantData={participantData}
          loading={registrationMutation.isPending}
        />
      )}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogContent?.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogContent?.content}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel}>
            {dialogContent?.cancelLabel}
          </Button>
          <Button onClick={handleDialogConfirm} autoFocus>
            {dialogContent?.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RegisterPage;
